import { Button, Colors, FieldWrapper, TextFieldWrapper, Table, TableCell, Badge } from "@factorearth/component-library";
import { Permission, User, UserForResolver } from "db/definitions";
import Form from "@factorearth/component-library/dist/Molecules/Form/Form";
import useCustomForm from "hooks/useCustomForm";
import React, { useEffect, useState } from "react";
import { v4 as uuid } from "uuid";
import { useTheme } from "hooks/useSingleton";

interface Props {
	createPermission: (permission: Permission) => Promise<void>;
	selectedPermission?: Permission;
	updatePermission: (permission: Permission) => Promise<void>;
	userDoc: UserForResolver | User;
	permissions: Permission[];
}

function PermissionForm(props: Props) {
	const { createPermission, selectedPermission, updatePermission, userDoc, permissions } = props;
	const [disabled, setDisabled] = useState(false);
	const [openForm, setForm] = useState(false);
	const [timeStamp, setTimeStamp] = useState(new Date().toISOString());
	const [initialValues, setInitialValues] = useState<Permission>({
		id: selectedPermission?.id || uuid(),
		name: selectedPermission?.name || "",
		description: selectedPermission?.description || "",
		value: selectedPermission?.value || "",
		group: selectedPermission?.group || userDoc.group,
		createdAt: selectedPermission?.createdAt || timeStamp,
		updatedAt: selectedPermission?.updatedAt || timeStamp,
	});

	const colorPalette = useTheme();

	useEffect(() => {
		setInitialValues({
			id: selectedPermission?.id || uuid(),
			name: selectedPermission?.name || "",
			description: selectedPermission?.description || "",
			value: selectedPermission?.value || "",
			group: selectedPermission?.group || userDoc.group,
			createdAt: selectedPermission?.createdAt || timeStamp,
			updatedAt: selectedPermission?.updatedAt || timeStamp,
		});
		setTimeStamp(new Date().toISOString());
	}, [openForm]);

	const { handleChange, handleSubmit, values } = useCustomForm(initialValues);

	function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
		const event = {
			...e,
			target: {
				...e.target,
				name: e.target.name as keyof Permission,
				value: e.target.value as Permission[keyof Permission],
			},
		};
		handleChange(event);
	}

	async function saveButton() {
		setDisabled(true);
		await handleSubmit();
		selectedPermission ? await updatePermission(values) : await createPermission(values);
		setDisabled(false);
		setForm(false);
	}

	const columnConfiguration = [
		{
			key: "name",
			header: "Permissions",
			sortable: false,
			cell: (row: any) => (
				<TableCell colorPalette={colorPalette} style={{ padding: "8px 0px" }} key={`cell-for-permission-${row.id}`}>
					<Badge theme={colorPalette} variant={"filled"} label={row.name} style={{ padding: "4px 8px" }} />
				</TableCell>
			),
		},
		// Delete Button
		// {
		// 	key: "permissions",
		// 	header: "Permissions",
		// 	cell: (row: any) => {
		// 		const roles: any = [];
		// 		row.permissions.forEach((permission: any) => roles.push(<Badge theme={colorPalette} variant={"success"} label={permission.name} />));
		// 		return <TableCell>{roles}</TableCell>;
		// 	},
		// 	sortable: false,
		// },
	];

	return (
		<>
			{openForm ? (
				<Form colors={colorPalette}>
					<FieldWrapper colorPalette={colorPalette} label="Name">
						<TextFieldWrapper colorPalette={colorPalette} onChange={handleInputChange} value={values.name} aria-label="Name input" name="name" />
					</FieldWrapper>
					<FieldWrapper colorPalette={colorPalette} label="Description">
						<TextFieldWrapper
							colorPalette={colorPalette}
							onChange={handleInputChange}
							value={values.description}
							aria-label="Description input"
							name="description"
						/>
					</FieldWrapper>
					<FieldWrapper colorPalette={colorPalette} label="Value">
						<TextFieldWrapper colorPalette={colorPalette} onChange={handleInputChange} value={values.value} aria-label="Value input" name="value" />
					</FieldWrapper>
					<FieldWrapper colorPalette={colorPalette} label="Group">
						<TextFieldWrapper
							colorPalette={colorPalette}
							onChange={handleInputChange}
							disabled
							value={values.group}
							aria-label="Group input"
							name="group"
						/>
					</FieldWrapper>
					<div style={{ display: "flex" }}>
						<Button
							colorPalette={colorPalette}
							variant="filled"
							style={{ alignSelf: "flex-end" }}
							onClick={() => setForm(false)}
							//@ts-ignore
							disabled={disabled}>
							Close
						</Button>
						<Button
							colorPalette={colorPalette}
							variant="filled"
							style={{ alignSelf: "flex-end" }}
							onClick={saveButton}
							//@ts-ignore
							disabled={disabled}>
							Create Permission
						</Button>
					</div>
				</Form>
			) : (
				<div style={{ width: "100%", background: colorPalette.background.primary }}>
					<div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
						<Button
							colorPalette={colorPalette}
							variant="outlined"
							onClick={() => setForm(true)}
							label="Create Permissions"
							style={{ margin: "24px" }}
						/>
					</div>
					<Table colorPalette={colorPalette} columnConfig={columnConfiguration} tableData={permissions} pageSize={5} />
				</div>
			)}
		</>
	);
}

export default PermissionForm;
